import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { RouterLink, Router } from "@angular/router";

import {
  Observable,
  Subject,
  BehaviorSubject,
  forkJoin,
  throwError,
} from "rxjs";
import { debounceTime, takeUntil, tap, catchError } from "rxjs/operators";
import * as FileSaver from "file-saver";

import { Course } from "./../shared/models/course";
import { NotificationsService } from "./../../shared/services/notifications.service";
import { UserService } from "./../../shared/services/user.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { PractitionerService } from "./../shared/services/practitioner.service";
import { PractitionerMocksService } from "./../../shared/services/mocks/practitioner-mocks.service";
import { CoursesComponent } from "./../credentials-maintenance/courses/courses.component";
import { DateUtils } from "app/shared/utils/date-utils";

@Component({
  selector: "app-credentials-maintenance",
  templateUrl: "./credentials-maintenance.component.html",
  styleUrls: ["./credentials-maintenance.component.scss"],
})
export class CredentialsMaintenanceComponent implements OnInit, OnDestroy {
  importCourseFormVisible = false;
  recertEligible: boolean;
  personId: string;
  ccpBegins: Date;
  ccpEnds: Date;
  ccpGraceEnds: Date;
  hoursAchieved: any;
  ccpEligible = false;
  ccpAudit = false;
  ccpStatus: any;
  cmpDashBoard: boolean;
  courses = new BehaviorSubject<Course[]>(null);
  onDestroy$ = new Subject();

  @ViewChild(CoursesComponent, { static: true })
  private course: CoursesComponent;

  constructor(
    private notifications: NotificationsService,
    private viewState: ViewStateService,
    private practitionerService: PractitionerService,
    private userService: UserService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.personId = this.userService.CurrentUser.personId;
    if (this.router.url.includes("default")) {
      this.getCredentialsData();
    } else {
      this.practitionerService
        .getProfile(this.personId)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((details) => {
          this.ccpBegins = DateUtils.isValid(details.ccpCycleBegin)
            ? DateUtils.parse(details.ccpCycleBegin)
            : null;
          this.ccpEnds = DateUtils.isValid(details.ccpCycleEnd)
            ? DateUtils.parse(details.ccpCycleEnd)
            : null;
          this.ccpGraceEnds = DateUtils.isValid(details.ccpGraceEnds)
            ? DateUtils.parse(details.ccpGraceEnds)
            : null;
          this.ccpStatus = details.ccpStatus;
          if (this.ccpStatus)
            this.ccpAudit = this.determineFlagForAudit(this.ccpStatus);
          this.cmpDashBoard = details.cmpDashBoard;
          this.ccpEligible = this.determineCCPEligibility();
          if (
            this.cmpDashBoard &&
            !this.ccpStatus.recertify &&
            !this.ccpStatus.reinstate
          ) {
            this.viewState.stopLoading();
            this.router.navigate(["assessment"]);
          } else {
            this.getCredentialsData();
          }
        });
    }
  }
  get isRecertify() {
    return (
      this.ccpStatus &&
      this.ccpStatus.recertify &&
      this.ccpStatus.renewalStatus == "OK" &&
      !this.cmpDashBoard
    );
  }

  getCredentialsData() {
    this.viewState.startLoading();
    forkJoin([
      this.practitionerService.getCCPInfo(this.personId),
      this.practitionerService.courseCompletions(this.personId),
      this.practitionerService.getProfile(this.personId),
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((res) => {
        this.hoursAchieved = res[0].ccpHoursAchieved
          ? res[0].ccpHoursAchieved[0]
          : [];
        this.recertEligible = this.determineRecertifyEligibility(res[0]);
        this.ccpAudit = this.determineFlagForAudit(res[0]);
        this.ccpBegins = DateUtils.isValid(res[2].ccpCycleBegin)
          ? DateUtils.parse(res[2].ccpCycleBegin)
          : null;
        this.ccpEnds = DateUtils.isValid(res[2].ccpCycleEnd)
          ? DateUtils.parse(res[2].ccpCycleEnd)
          : null;
        this.ccpGraceEnds = DateUtils.isValid(res[2].ccpGraceEnds)
          ? DateUtils.parse(res[2].ccpGraceEnds)
          : null;

        this.ccpEligible = this.determineCCPEligibility();
        this.ccpStatus = res[2].ccpStatus;
        this.courses.next(res[1].filter((c) => c.courseName !== null));
        this.viewState.stopLoading();
      });
  }

  private determineRecertifyEligibility(info: any): boolean {
    return (info.reinstate || info.recertify) && info.renewalStatus === "OK";
  }

  private determineCCPEligibility(): boolean {
    if (!this.ccpBegins && !this.ccpEnds && !this.ccpAudit) return false;
    else return true;
  }

  private determineFlagForAudit(info: any): boolean {
    return info.flagForAudit;
  }

  deleteCourse(courseId: string) {
    if (window.confirm("Are you sure you want to delete this course?")) {
      this.viewState.startLoading();
      this.practitionerService
        .deleteCourse(this.personId, courseId)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (resp) => {
            this.notifications.success("Success", "The course was deleted.");
            this.getCredentialsData();
          },
          (error) => {
            this.notifications.error("Failed", "The course was not deleted.");
            this.viewState.stopLoading();
          },
        );
    }
  }

  openImportCourseForm() {
    this.importCourseFormVisible = true;
  }

  performRecertificationTask() {
    this.router.navigate(["purchase/checkout/certification"], {
      queryParams: { isRecertify: this.isRecertify },
    });
  }

  closeForm() {
    this.importCourseFormVisible = false;
  }

  private generateCEReport() {
    this.practitionerService
      .generateCEReport(this.personId)
      .pipe(
        catchError((e) => {
          this.handleError(e);
          return throwError(e);
        }),
        tap((response) => {
          if (response instanceof Blob) {
            this.openFile(response);
          } else {
            this.notifications.error("Error", "Invalid response from server.");
          }
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.notifications.success("Success", "Report was downloaded.");
        this.viewState.stopLoading();
      });
  }

  handleError(error: any): Observable<any> {
    this.notifications.error(
      "Failed",
      "Report did not generate. Try again or contact NBRC if the problem continues.",
    );
    this.viewState.stopLoading();
    return throwError(error);
  }

  private openFile(stream: any) {
    let blob = new Blob([stream], { type: "application/pdf" });
    let fileName = "CE-Report";
    fileName += ".pdf";

    FileSaver.saveAs(blob, fileName);
  }

  ceuBarWidth(completed: number, required: number): number {
    if (!required) return 0;
    return Math.min(100, Math.round(((completed || 0) / required) * 100));
  }

  ceuMet(completed: number, required: number): boolean {
    return required > 0 && (completed || 0) >= required;
  }

  ceuBarColor(
    completed: number,
    required: number,
    categoryColor: string,
  ): string {
    return required > 0 && (completed || 0) >= required
      ? "#28a745"
      : categoryColor;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
